// @workflow platform.viewer
/**
 * The 360 mini map's operations on the 2D plan (AVA-660). The page keeps ONE plan viewer, its DOM
 * node parked in the store (GET_PAGE_VIEW_2D, re-attached to #planViewer when a 2D pane mounts).
 * The mini map borrows that node while no 2D pane is visible and hands it back when one is shown;
 * slots are shown with v-show, so nothing remounts and the hand-back is ours to do.
 * No Vue or path-alias imports (node --test loads this file).
 */
import { planSpan } from '../../features/Dashboard/utils/miniMap.js';

/** MainView has four view slots (store/modules/viewers.js view1Type..view4Type). */
const SLOT_COUNT = 4;

/** @param {object} ctx see core.js `createViewer` */
export function createMiniMapOps(ctx) {
  const { store, deps } = ctx;
  const planViewer = () => store.state.planViewer || null;
  const isMapped = (level) => !!(level && level.planUrn && level.hasAllMappingInfo && level.hasAllMappingInfo());

  return {
    /** A MainView slot of type '2d' is visible (reads store getters, so it is reactive in a computed). */
    planPaneVisible() {
      for (let slot = 1; slot <= SLOT_COUNT; slot += 1) {
        if (store.getters[`GET_VIEW_${slot}_TYPE`] === '2d' && store.getters[`GET_IS_VIEW_${slot}_VISIBLE`]) return true;
      }
      return false;
    },
    /** The level a scan is on (AVA-597 scan index), or null when it is on none. */
    levelOfScan(scanId) {
      const entry = (store.state.scanIndex?.entries || []).find((e) => e.id === scanId);
      if (!entry) return null;
      return (store.state.levels || []).find((level) => level.id === entry.levelId) || null;
    },
    /** @returns {'unmapped' | 'missing' | 'other-level' | 'ready'} whether the plan can show `level` now */
    planReadyFor(level) {
      if (!isMapped(level)) return 'unmapped';
      if (!planViewer()) return 'missing';
      return store.state.selectedLevel === level ? 'ready' : 'other-level';
    },
    /** Loads `level`'s plan straight into `container` (launchPlanViewer with a parent); false when it failed. */
    async loadPlanInto(container, level) {
      store.commit('setSelectedLevel', level);
      try {
        await deps.plan.launch(level.planUrn, deps.plan.streamingApiFor(level.apsRegion), container);
        return true;
      } catch (err) {
        deps.plan.reportError(err);
        return false;
      }
    },
    /** Moves the stored plan node into `container`; false when there is none yet. */
    borrowPlanInto(container) {
      const node = store.getters.GET_PAGE_VIEW_2D;
      if (!node || !container) return false;
      if (node.parentNode !== container) container.appendChild(node);
      return true;
    },
    /** Moves the plan node back into the 2D pane's #planViewer, or only detaches it when there is none. */
    returnPlanToPane() {
      const node = store.getters.GET_PAGE_VIEW_2D;
      if (!node) return false;
      const pane = deps.plan.paneHost();
      if (pane) {
        // Same placement as pageView's restore (pageView.vue:1564-1592).
        if (pane.firstChild) pane.insertBefore(node, pane.firstChild);
        else pane.appendChild(node);
      } else if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
      return true;
    },
    /**
     * Switches the plan's mini map look (tags off, bubbles unclustered, 360 marker). A store flag, not
     * an extension field: launchPlanViewer resolves before labels3dExt / Plan2DExtension finish
     * loading, and an extension that loads later reads the flag in its first draw.
     */
    setPlanMiniMapMode(on) {
      store.commit('SET_PLAN_MINI_MAP_MODE', !!on);
      this.refreshPlanMiniMap();
    },
    /** Redraws the plan's scans and marker for the mini map (another scan opened: fading changes). */
    refreshPlanMiniMap() {
      const viewer = planViewer();
      if (!viewer) return;
      for (const id of ['labels3dExt', 'Plan2DExtension']) {
        const ext = viewer.getExtension(id);
        if (ext && typeof ext.refreshForMiniMap === 'function') ext.refreshForMiniMap();
      }
    },
    /** Frames `spanMetres` of plan around a world point (metres in, like the level mapping). */
    centrePlanOn(worldPoint, spanMetres) {
      const viewer = planViewer();
      const level = store.state.selectedLevel;
      if (!viewer || !isMapped(level) || !level.mappingViewersPointsInfo) return false;
      const to2d = (p) => level.mappingViewersPointsInfo._3dTo2d(p);
      const centre = to2d(worldPoint);
      const half = planSpan(to2d, worldPoint, spanMetres) / 2;
      deps.plan.fitBox(viewer, {
        min: { x: centre.x - half, y: centre.y - half },
        max: { x: centre.x + half, y: centre.y + half },
      });
      return true;
    },
    /** The 360's hotspot rule for `image`: its range and the scans within it (forgeService, AVA-660). */
    scansIn360Range(image) {
      const service = ctx.service();
      return { range: service.hotspotRange(), scans: service.scansInHotspotRange(image) };
    },
    /**
     * The plan's 360 icon nearest a canvas point (within half an icon) and where it is drawn,
     * for the mini map's hover tip; null when there is none.
     * @returns {{ image: object, x: number, y: number } | null}
     */
    planScanAt(x, y) {
      const viewer = planViewer();
      const labels = viewer && viewer.getExtension('labels3dExt');
      const hit = labels && labels.nearestIconOnScreen(x, y);
      if (!hit) return null;
      const data = hit.pointCloud.forgeData[hit.index];
      const screen = viewer.worldToClient(data.point);
      return { image: data.object, x: screen.x, y: screen.y };
    },
    /** Where a world point (scan, reference point) is drawn on the plan canvas, or null when it cannot be. */
    planScreenPoint(worldPoint) {
      const viewer = planViewer();
      const level = store.state.selectedLevel;
      if (!worldPoint || !viewer || !isMapped(level) || !level.mappingViewersPointsInfo) return null;
      const onPlan = level.mappingViewersPointsInfo._3dTo2d(worldPoint);
      if (!onPlan) return null;
      const screen = viewer.worldToClient(onPlan);
      return { x: screen.x, y: screen.y };
    },
    /** Calls `handler` whenever the plan's camera moves (pan, zoom, framing); returns the unsubscribe. */
    onPlanCameraChange(handler) {
      const viewer = planViewer();
      if (!viewer) return () => {};
      const event = deps.events.CAMERA_CHANGE;
      viewer.addEventListener(event, handler);
      return () => viewer.removeEventListener(event, handler);
    },
    /** The open 360's horizontal field of view in degrees, or null without a panorama. */
    panoramaHfov() {
      const panorama = ctx.service().panoramaViewer;
      return panorama ? panorama.getHfov() : null;
    },
    /** Opens a scan in the views, as clicking it anywhere else does. */
    openScanFromMap(image) {
      ctx.service().showImageInViews(image);
    },
    /** A length in the model's display unit (the 360 legend's format). */
    formatLength(value) {
      return ctx.service().formatValueToDisplayUnit(value);
    },
  };
}
